import { toast } from '@/components/ui/use-toast';
import { downloadFilefromBuffer } from '@/lib/utils';
import type {
  CaseDocumentTypeGetAllResponse,
  CaseDocumentTypeQueryRequest,
  CaseDocumentTypeResponse,
} from '@/types/case.types';
import {
  ServiceCreateResponse,
  ServiceCreateUpdateRequest,
  ServiceGetAllRequestBody,
  ServiceGetAllResponse,
  ServiceResponse,
  ServiceSessionResponse,
} from '@/types/service.types';
import {
  type AffidavitTypeGetAllResponse,
  type AffidavitTypeQueryRequest,
  WorkOrderPatchRequest,
  type WorkOrderResponse,
} from '@/types/workOrder.types';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import type { GenericErrorResponse, GenericErrorResponseError } from '@/types/api.types';
import axiosInstance from '@/services/axiosInstance';
import { request } from '@/services/request';

export const usePatchWorkOrder = () => {
  return useMutation<
    WorkOrderResponse,
    AxiosError<GenericErrorResponse>,
    { workOrderId: string; data?: WorkOrderPatchRequest }
  >({
    mutationFn: async ({ workOrderId, data }: { workOrderId: string; data?: WorkOrderPatchRequest }) => {
      const response = await axios.patch<WorkOrderResponse>(`/work-orders/${workOrderId}`, data);
      return response.data;
    },
  });
};

export const getAllServices = async (
  requestBody: ServiceGetAllRequestBody
): Promise<{
  totalRows: number;
  services: ServiceResponse[];
}> => {
  const response = await axios.post<ServiceGetAllResponse>(`/services/getAll`, requestBody);

  return {
    totalRows: response.data.totalRows,
    services: response.data.services,
  };
};

export const downloadWorkOrderPDF = async (
  workOrderId: string,
  workOrderNumber?: string
): Promise<boolean> => {
  toast({
    description: 'Download will start shortly',
    duration: 3000,
  });
  try {
    const response = await axios.get<Blob>(`/work-orders/${workOrderId}/pdf`, {
      responseType: 'blob',
    });
    downloadFilefromBuffer(response.data, `${workOrderNumber}.pdf`, 'PDF');
    return true;
  } catch (err) {
    toast({
      description: 'Unable to generate PDF, please try again',
      duration: 3000,
      variant: 'destructive',
    });
    return false;
  }
};

export const useCreateService = () => {
  return useMutation<ServiceResponse, GenericErrorResponse, { data: ServiceCreateUpdateRequest }>({
    mutationFn: async ({ data }) => {
      const response = await axios.post<ServiceResponse>(`/services`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const useUpdateServiceById = () => {
  return useMutation<ServiceResponse, GenericErrorResponse, { serviceId?: string; data: ServiceCreateUpdateRequest }>({
    mutationFn: async ({ serviceId, data }) => {
      const response = await axios.put<ServiceResponse>(`/services/${serviceId}`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const useCreateDocument = ()=>  {
return useMutation<CaseDocumentTypeResponse, AxiosError<GenericErrorResponse>, { caseTypeId: string; documentName: string }>({
    mutationFn: async (data: { caseTypeId: string; documentName: string }) => {
      const response = await axios.post<CaseDocumentTypeResponse>('/cases/case-document-types', data);
      return response.data;
    },
  })}

export const CASE_DOCUMENT_TYPES_QUERY_KEY = 'caseDocumentTypes';

export const getCaseDocumentTypes = async (caseTypeId: string, search?: string) => {
  const response = await axiosInstance.get<CaseDocumentTypeGetAllResponse>('/cases/case-document-types', {
    params: { caseTypeId, search: search ? search : undefined, sortField: 'docType', sortDirection: 'asc' },
  });
  return response.data;
};

// `search` is sent to the API (DGR-1468). While the next search loads, the previous results stay on
// screen, but only for the same case type, so another case type's documents are never shown or picked.
export const useCaseDocumentTypes = (caseTypeId: string | undefined, search = '') =>
  useQuery<CaseDocumentTypeGetAllResponse, AxiosError<GenericErrorResponse>>({
    queryKey: [CASE_DOCUMENT_TYPES_QUERY_KEY, caseTypeId, search],
    queryFn: () => getCaseDocumentTypes(caseTypeId!, search),
    enabled: !!caseTypeId,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === caseTypeId ? previous : undefined),
    staleTime: 1000 * 60 * 5,
  });

export const useGetServiceById = (serviceId: string | undefined) => {
  return useQuery<AxiosResponse<ServiceResponse>, GenericErrorResponseError>({
    queryKey: ['service', serviceId],
    queryFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.get<ServiceResponse>(`/services/${serviceId}`);
      return response;
    },
    enabled: !!serviceId,
  });
};

export const useGetServiceSession = (serviceId: string | undefined) => {
  return useQuery<AxiosResponse<ServiceSessionResponse>, GenericErrorResponseError>({
    queryKey: ['service-session', serviceId],
    queryFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.get<ServiceSessionResponse>(`/services/${serviceId}/session`);
      return response;
    },
    enabled: !!serviceId,
  });
};

export const useCreateServiceSession = (serviceId: string | undefined) => {
  return useMutation<ServiceCreateResponse, GenericErrorResponse>({
    mutationFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.post<ServiceCreateResponse>(`/services/${serviceId}/session`);
      return response.data;
    },
  });
};

export const useRefreshServiceSession = (serviceId: string | undefined) => {
  return useMutation<ServiceCreateResponse, GenericErrorResponse>({
    mutationFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.patch<ServiceCreateResponse>(`/services/${serviceId}/session/refresh`);
      return response.data;
    },
  });
};

export const useEndServiceSession = (serviceId: string | undefined) => {
  return useMutation<ServiceCreateResponse, GenericErrorResponse>({
    mutationFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.patch<ServiceCreateResponse>(`/services/${serviceId}/session/end`);
      return response.data;
    },
  });
};

export const fetchAffidavitTypes = (params?: AffidavitTypeQueryRequest) =>
  request(() => axiosInstance.get<AffidavitTypeGetAllResponse>('/affidavit-types/', { params }));

export const fetchCaseDocumentTypes = (params?: CaseDocumentTypeQueryRequest) =>
  request(() => axiosInstance.get<CaseDocumentTypeGetAllResponse>('/cases/case-document-types/', { params }));
